/**
 * Filename and version-name rules, kept in a module of their own so they can be unit tested
 * without a browser. Getting these wrong silently mis-files an upload or shows the wrong
 * version as "the latest", which is exactly the kind of thing nobody notices in a review.
 */

import type { VersionStatus } from './types.ts';

/**
 * Split a filename into shot + version. A version token (`_v02`, `.v7`, `-v012`) marks the
 * boundary: everything before it is the shot (FRW_145_010_v02.mov -> shot FRW_145_010,
 * version v02), so two versions of the same shot are recognised as one shot. Without a
 * version token the whole basename is the shot and the server assigns the next version.
 *
 * The digits are kept as written (padded to at least two) rather than normalised to the
 * server's v001 form: the name in the UI should be the name on the file. Ordering does not
 * depend on the spelling — the server stores a numeric version alongside it.
 */
export function parseShotVersion(filename: string): { shotName: string; versionName?: string } {
  const base = filename.replace(/\.[^.]+$/, '');
  const m = /^(.*?)[._-]v(\d+)(?=[._-]|$)/i.exec(base);
  if (m && m[1]) return { shotName: m[1], versionName: `v${(m[2] ?? '').padStart(2, '0')}` };
  return { shotName: base };
}

/**
 * Ascending version order, so the last entry of a group is the newest. Sorting by name would
 * put v9 after v100; `versionNumber` comes from the server and counts versions properly.
 */
export function compareVersions(
  a: { versionNumber: number; name: string },
  b: { versionNumber: number; name: string },
): number {
  return a.versionNumber - b.versionNumber || a.name.localeCompare(b.name);
}

/**
 * Human-natural string order: SHOT_9 sorts before SHOT_100, not after. Plain `localeCompare`
 * would compare digit by digit; the `numeric` option treats runs of digits as numbers. Used to
 * order shot *names* (a version's numeric order comes from `compareVersions` instead).
 */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * The status of a shot across all its versions: approved if *any* version is approved, else
 * rejected if any is rejected, else pending — and the highest-numbered version carrying that
 * status. `versions` is expected ascending (see compareVersions), so the last match is the
 * highest. This is what the project list shows per clip, independent of which version is
 * currently selected in the row.
 */
export function aggregateStatus<T extends { status: VersionStatus }>(
  versions: T[],
): { status: VersionStatus; version: T | null } {
  const highest = (s: VersionStatus): T | null => versions.filter((v) => v.status === s).at(-1) ?? null;
  const approved = highest('approved');
  if (approved) return { status: 'approved', version: approved };
  const rejected = highest('rejected');
  if (rejected) return { status: 'rejected', version: rejected };
  return { status: 'pending', version: null };
}
